// ========================================
// CONFIGURATION 
// ========================================
const PROSHIP_CONFIG = {
    LOGIN_API_URL: 'https://proshipdev.prozo.com/api/auth/signin',
    SERVICEABILITY_API_URL: 'https://proshipdev.prozo.com/api/tools/serviceability',
    TAT_API_URL: 'https://rqkp8eydpl.execute-api.ap-south-1.amazonaws.com/prod/proship-tat',
    WAREHOUSES: [
        { name: 'Bangalore', pincode: 562111 },
        { name: 'Haryana', pincode: 122413 }
    ],
    LOGIN_CREDENTIALS: {
        username: 'test.staging@prozo.com',
        password: 'Hello@123',
        rememberMe: false
    },
    TOKEN_REFRESH_INTERVAL: 59 * 60 * 1000,
    STORAGE_KEYS: {
        TOKEN: 'proship_bearer_token',
        EXPIRY: 'proship_token_expiry'
    }
};

let pincodeInput, checkBtn, resultDiv, loadingDiv;
let tokenRefreshInterval = null;

// ========================================
// TOKEN MANAGEMENT
// ========================================

function getStoredToken() {
    return localStorage.getItem(PROSHIP_CONFIG.STORAGE_KEYS.TOKEN);
}

function getTokenExpiry() {
    const expiry = localStorage.getItem(PROSHIP_CONFIG.STORAGE_KEYS.EXPIRY);
    return expiry ? parseInt(expiry) : null;
}

function isTokenValid() {
    const token = getStoredToken();
    const expiry = getTokenExpiry();
    if (!token || !expiry) return false;
    return Date.now() < (expiry - 60000);
}

function storeToken(token) {
    const expiryTime = Date.now() + (60 * 60 * 1000);
    localStorage.setItem(PROSHIP_CONFIG.STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(PROSHIP_CONFIG.STORAGE_KEYS.EXPIRY, expiryTime.toString());
}

function clearToken() {
    localStorage.removeItem(PROSHIP_CONFIG.STORAGE_KEYS.TOKEN);
    localStorage.removeItem(PROSHIP_CONFIG.STORAGE_KEYS.EXPIRY);
}

async function fetchNewToken() {
    try {
        const response = await fetch(PROSHIP_CONFIG.LOGIN_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(PROSHIP_CONFIG.LOGIN_CREDENTIALS)
        });

        if (!response.ok) {
            throw new Error(`Login failed: ${response.status}`);
        }

        const data = await response.json();
        const token = data.accessToken;

        if (!token) {
            throw new Error('No access token received');
        }

        storeToken(token);
        return token;
    } catch (error) {
        throw error;
    }
}

async function ensureValidToken() {
    if (isTokenValid()) {
        return getStoredToken();
    }
    return await fetchNewToken();
}

function startTokenRefreshLoop() {
    if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
    }

    fetchNewToken().catch(() => { });

    tokenRefreshInterval = setInterval(async () => {
        try {
            await fetchNewToken();
        } catch (error) { }
    }, PROSHIP_CONFIG.TOKEN_REFRESH_INTERVAL);
}

function stopTokenRefreshLoop() {
    if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
    }
}

// ========================================
// INITIALIZATION
// ========================================
document.addEventListener('DOMContentLoaded', () => {
    pincodeInput = document.getElementById('delivery-pincode');
    checkBtn = document.getElementById('check-pincode-btn');
    resultDiv = document.getElementById('pincode-result');
    loadingDiv = document.getElementById('pincode-loading');

    if (!pincodeInput || !checkBtn) return;

    startTokenRefreshLoop();

    checkBtn.addEventListener('click', () => handleCheckPincode());

    pincodeInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCheckPincode();
        }
    });

    pincodeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '');
    });
});

window.addEventListener('beforeunload', () => stopTokenRefreshLoop());

// ========================================
// VALIDATION & SERVICEABILITY HELPER
// ========================================
function validatePincode(pincode) {
    return /^\d{6}$/.test(pincode);
}

function isTATServiceable(tatData) {
    if (!tatData) return false;

    if (tatData.courierWiseTAT && tatData.courierWiseTAT.length > 0) {
        const courier = tatData.courierWiseTAT[0];
        if (courier.edd || courier.minDays != null || courier.maxDays != null) {
            return true;
        }
    }

    if (tatData.overallMinTAT != null || tatData.overallMaxTAT != null) {
        return true;
    }

    return false;
}

// ========================================
// MAIN HANDLER
// ========================================
async function handleCheckPincode() {
    const pincode = pincodeInput.value.trim();

    if (!validatePincode(pincode)) {
        showError('Please enter a valid 6-digit PIN code');
        return;
    }

    showLoading();

    try {
        const warehouseChecks = await Promise.all(
            PROSHIP_CONFIG.WAREHOUSES.map(async (warehouse) => {
                try {
                    let serviceResult = null;
                    try {
                        serviceResult = await checkServiceability(pincode, warehouse.pincode);
                    } catch (e) {
                        console.warn('Serviceability API call error:', e);
                    }

                    const tatResult = await getTATEstimate(pincode, warehouse.pincode);

                    const isServiceableViaService = serviceResult && Array.isArray(serviceResult.result) && serviceResult.result.length > 0;
                    const isServiceableViaTAT = isTATServiceable(tatResult);

                    if (!isServiceableViaService && !isServiceableViaTAT) {
                        return null;
                    }

                    return { warehouse, serviceResult, tatResult };
                } catch (error) {
                    return null;
                }
            })
        );

        const serviceableWarehouses = warehouseChecks.filter(check => check !== null);

        if (serviceableWarehouses.length === 0) {
            showNotServiceable();
            return;
        }

        const bestWarehouse = selectBestWarehouse(serviceableWarehouses);
        displayResult(bestWarehouse.serviceResult, bestWarehouse.tatResult, bestWarehouse.warehouse);
    } catch (error) {
        showError('Unable to check delivery availability. Please try again later.');
    }
}

// ========================================
// SELECT BEST WAREHOUSE
// ========================================
function selectBestWarehouse(serviceableWarehouses) {
    if (serviceableWarehouses.length === 1) {
        return serviceableWarehouses[0];
    }

    let bestWarehouse = serviceableWarehouses[0];
    let earliestEDD = null;

    for (const warehouse of serviceableWarehouses) {
        const tatData = warehouse.tatResult;

        if (tatData && tatData.courierWiseTAT && tatData.courierWiseTAT.length > 0) {
            const courier = tatData.courierWiseTAT[0];

            if (courier.edd) {
                const eddDate = new Date(courier.edd);
                if (!earliestEDD || eddDate < earliestEDD) {
                    earliestEDD = eddDate;
                    bestWarehouse = warehouse;
                }
            } else if (courier.minDays) {
                if (!earliestEDD) {
                    earliestEDD = courier.minDays;
                    bestWarehouse = warehouse;
                } else if (typeof earliestEDD === 'number' && courier.minDays < earliestEDD) {
                    earliestEDD = courier.minDays;
                    bestWarehouse = warehouse;
                }
            }
        } else if (tatData && tatData.overallMinTAT) {
            if (!earliestEDD || (typeof earliestEDD === 'number' && tatData.overallMinTAT < earliestEDD)) {
                earliestEDD = tatData.overallMinTAT;
                bestWarehouse = warehouse;
            }
        }
    }

    return bestWarehouse;
}

// ========================================
// UI STATE FUNCTIONS
// ========================================
function showLoading() {
    if (loadingDiv) loadingDiv.classList.remove('hidden');
    if (resultDiv) resultDiv.classList.add('hidden');
}

function hideLoading() {
    if (loadingDiv) loadingDiv.classList.add('hidden');
}

function showError(message) {
    hideLoading();
    resultDiv.classList.remove('hidden');
    resultDiv.innerHTML = `
    <div class="pincode-error">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <div>
        <strong>Invalid PIN code</strong>
        <p>${message}</p>
      </div>
    </div>
  `;
}

function showNotServiceable() {
    hideLoading();
    resultDiv.classList.remove('hidden');
    resultDiv.innerHTML = `
    <div class="pincode-error">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
      </svg>
      <div>
        <strong>Currently unavailable</strong>
        <p>We don't deliver to this location yet. Please check back soon or contact us for assistance.</p>
      </div>
    </div>
  `;
}

// ========================================
// SERVICEABILITY API
// ========================================
async function checkServiceability(pincode, warehousePincode, retryCount = 0) {
    const requestBody = [{
        drop_pincode: parseInt(pincode),
        pickup_pincode: warehousePincode
    }];

    const token = await ensureValidToken();

    const response = await fetch(PROSHIP_CONFIG.SERVICEABILITY_API_URL, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
    });

    if (response.status === 401 && retryCount === 0) {
        clearToken();
        await fetchNewToken();
        return checkServiceability(pincode, warehousePincode, 1);
    }

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'API request failed');
    }

    const data = await response.json();

    console.log("DATA:", data);

    if (!data.meta || !data.meta.success) {
        throw new Error(data.meta?.message || 'API request failed');
    }

    return data;
}

// ========================================
// TAT API
// ========================================
async function getTATEstimate(pincode, warehousePincode) {
    const requestBody = {
        queryStringParam: {
            drop_pincode: parseInt(pincode),
            pickup_pincode: warehousePincode,
            merchantid: "674983decbcf757609b67f3e"
        }
    };

    try {
        const response = await fetch(PROSHIP_CONFIG.TAT_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) return null;

        const data = await response.json();

        // Debug logging
        console.log(`📦 TAT API Response for ${warehousePincode} → ${pincode}:`, {
            overallMinTAT: data.overallMinTAT,
            overallMaxTAT: data.overallMaxTAT,
            courierWiseTAT: data.courierWiseTAT?.map(c => ({
                courierName: c.courierName,
                minDays: c.minDays,
                maxDays: c.maxDays,
                edd: c.edd
            }))
        });

        return data;
    } catch (error) {
        return null;
    }
}

// ========================================
// DISPLAY RESULT
// ========================================
function displayResult(serviceData, tatData, warehouse) {
    hideLoading();
    resultDiv.classList.remove('hidden');

    const isServiceableViaService = serviceData && Array.isArray(serviceData.result) && serviceData.result.length > 0;
    const isServiceableViaTAT = isTATServiceable(tatData);

    if (!isServiceableViaService && !isServiceableViaTAT) {
        showNotServiceable();
        return;
    }

    let deliveryDateText = '';

    const checkerSection = resultDiv ? resultDiv.closest('.pincode-checker-section') : null;
    const templateSuffix = (checkerSection?.getAttribute('data-template-suffix') || '').toLowerCase();
    const templateName = (checkerSection?.getAttribute('data-template') || '').toLowerCase();
    const isComingSoon = templateSuffix.includes('coming-soon') || templateName.includes('coming-soon');

    if (isComingSoon) {
        deliveryDateText = '10-15 business days';
    } else if (tatData && tatData.courierWiseTAT && tatData.courierWiseTAT.length > 0) {
        const courier = tatData.courierWiseTAT[0];

        if (courier.edd) {
            const eddDate = new Date(courier.edd);
            eddDate.setDate(eddDate.getDate() + 1);
            const options = { weekday: 'short', month: 'short', day: 'numeric' };
            deliveryDateText = `By ${eddDate.toLocaleDateString('en-IN', options)}`;
        } else if (courier.minDays != null && courier.maxDays != null) {
            deliveryDateText = `${courier.minDays}-${courier.maxDays} business days`;
        } else if (courier.minDays != null) {
            deliveryDateText = `${courier.minDays} business days`;
        }
    } else if (tatData && tatData.overallMinTAT != null && tatData.overallMaxTAT != null) {
        deliveryDateText = `${tatData.overallMinTAT}-${tatData.overallMaxTAT} business days`;
    } else if (tatData && tatData.overallMinTAT != null) {
        deliveryDateText = `${tatData.overallMinTAT} business days`;
    }

    if (!deliveryDateText) {
        deliveryDateText = '3-5 business days';
    }

    resultDiv.innerHTML = `
    <div class="pincode-success">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <div>
        <strong>Delivery Available!</strong>
        <p class="delivery-info">
          <span class="delivery-days">Expected delivery: ${deliveryDateText}</span>
        </p>
      </div>
    </div>
  `;
}
